import { CONFIG } from '../lib/config';
import { addBreadcrumb, reportToSentry } from '../lib/sentry';
import { isAbortError, isAuthSessionMissingError } from './errorGuards';

const isDev = CONFIG.isDevelopment;

/**
 * CSS color styles for console logs with background colors for better visibility in dark mode
 * Colors have been adjusted to be more visually distinguishable from each other
 */
const colors = {
  viewport:
    'background: #8e44ad; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;', // Deep Purple
  component:
    'background: #2471A3; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;', // Darker Blue
  store:
    'background: #138D75; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;', // Darker Teal
  refresh:
    'background: #E74C3C; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;', // Bright Red
  fetch:
    'background: #229954; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;', // Forest Green
  ai: 'background: #F39C12; color: black; font-weight: bold; padding: 2px 5px; border-radius: 3px;', // Gold (with black text)
  realtime:
    'background: #7D3C98; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;', // Indigo
  auth: 'background: #17A589; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;', // Green Cyan
  capacitor:
    'background: #5D6D7E; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;', // Steel Blue
  connectivity:
    'background: #3498DB; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;', // Blue
  sync: 'background: #16A085; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;', // Turquoise
  error:
    'background: #C0392B; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;', // Dark Red
  warn: 'background: #F1C40F; color: black; font-weight: bold; padding: 2px 5px; border-radius: 3px;', // Yellow (with black text)
  info: 'background: #3498DB; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;', // Royal Blue
  default: 'color: inherit;', // Default text color
};

/**
 * Enhanced logger with color-coded output and Sentry integration
 */
export const logger = {
  viewport: (message: string, data?: unknown) => {
    if (isDev) {
      if (data) {
        console.log(`📱 %cViewport%c ${message}`, colors.viewport, colors.default, data);
      } else {
        console.log(`📱 %cViewport%c ${message}`, colors.viewport, colors.default);
      }
    }
    // No Sentry breadcrumbs for routine viewport logs
  },

  /**
   * Log Capacitor-related operations
   * Used for tracking Capacitor initialization, plugin usage, and mobile-specific functionality
   */
  capacitor: (message: string, data?: unknown) => {
    if (isDev) {
      if (data) {
        console.log(`📱 %cCapacitor%c ${message}`, colors.capacitor, colors.default, data);
      } else {
        console.log(`📱 %cCapacitor%c ${message}`, colors.capacitor, colors.default);
      }
    }
    // No Sentry breadcrumbs for routine capacitor logs
  },

  component: (message: string, data?: unknown) => {
    if (isDev) {
      if (data) {
        console.log(`🧩 %cComponent%c ${message}`, colors.component, colors.default, data);
      } else {
        console.log(`🧩 %cComponent%c ${message}`, colors.component, colors.default);
      }
    }
    // No Sentry breadcrumbs for routine component logs
  },

  store: (message: string, data?: unknown) => {
    if (isDev) {
      if (data) {
        console.log(`📦 %cStore%c ${message}`, colors.store, colors.default, data);
      } else {
        console.log(`📦 %cStore%c ${message}`, colors.store, colors.default);
      }
    }
    // No Sentry breadcrumbs for routine store logs
  },

  ai: (message: string, data?: unknown) => {
    if (isDev) {
      if (data) {
        console.log(`🤖 %cAI%c ${message}`, colors.ai, colors.default, data);
      } else {
        console.log(`🤖 %cAI%c ${message}`, colors.ai, colors.default);
      }
    }
    // No Sentry breadcrumbs for routine AI logs
  },

  /**
   * Log authentication operations
   * Used for tracking auth state changes, sign-in/sign-out events, and auth initialization
   */
  auth: (message: string, data?: unknown) => {
    if (isDev) {
      if (data) {
        console.log(`🔐 %cAuth%c ${message}`, colors.auth, colors.default, data);
      } else {
        console.log(`🔐 %cAuth%c ${message}`, colors.auth, colors.default);
      }
    }
    // No Sentry breadcrumbs for routine auth logs
  },

  /**
   * Log realtime subscription operations
   * Used for tracking Supabase realtime subscriptions and updates
   */
  realtime: (message: string, data?: unknown) => {
    if (isDev) {
      if (data) {
        console.log(`📡 %cRealtime%c ${message}`, colors.realtime, colors.default, data);
      } else {
        console.log(`📡 %cRealtime%c ${message}`, colors.realtime, colors.default);
      }
    }
    // No Sentry breadcrumbs for routine realtime logs
  },

  /**
   * Log data fetch operations
   * Used for tracking API calls and data fetching operations
   */
  fetch: (message: string, data?: unknown) => {
    if (isDev) {
      if (data) {
        console.log(`📥 %cFetch%c ${message}`, colors.fetch, colors.default, data);
      } else {
        console.log(`📥 %cFetch%c ${message}`, colors.fetch, colors.default);
      }
    }
    // No Sentry breadcrumbs for routine fetch logs
  },

  /**
   * Log an informational message
   */
  // info logger has been intentionally removed

  /**
   * Log a warning message
   */
  warn: (message: string, data?: unknown) => {
    if (isDev) {
      if (data) {
        console.warn(`⚠️ %cWarning%c ${message}`, colors.warn, colors.default, data);
      } else {
        console.warn(`⚠️ %cWarning%c ${message}`, colors.warn, colors.default);
      }
    }

    // Add breadcrumb to Sentry with warning level
    addBreadcrumb(message, 'warning', 'warning', data as Record<string, unknown>);
  },

  /**
   * Log an error message and report to Sentry in production
   */
  error: (message: string, error?: Error | unknown, context?: Record<string, unknown>) => {
    // Skip logging expected/benign errors using centralized guards
    if (isAuthSessionMissingError(error)) {
      if (isDev && localStorage.getItem('ZENTIME_VERBOSE_DEBUG') === 'true') {
        console.log(
          `ℹ️ %cInfo%c Auth session missing (expected for logged-out users)`,
          colors.info,
          colors.default
        );
      }
      return;
    }

    if (isAbortError(error)) {
      if (isDev && localStorage.getItem('ZENTIME_VERBOSE_DEBUG') === 'true') {
        console.log(
          `ℹ️ %cInfo%c Request aborted (expected during navigation)`,
          colors.info,
          colors.default
        );
      }
      return;
    }

    // Always log errors to console in development
    if (isDev) {
      // Use console.error for the error icon but with our custom styling
      if (error) {
        console.error(`❌ %cError%c ${message}`, colors.error, colors.default, error);
      } else {
        console.error(`❌ %cError%c ${message}`, colors.error, colors.default);
      }
    }

    // Merge message with context for Sentry
    const sentryContext = {
      ...context,
      message,
      timestamp: new Date().toISOString(),
    };

    // Report to Sentry
    if (error) {
      reportToSentry(error, sentryContext);
    } else {
      reportToSentry(new Error(message), sentryContext);
    }
  },

  /**
   * Set user information for Sentry tracking
   */
  setUser: (user: { id: string; email?: string; username?: string }) => {
    // This is a no-op in the logger itself, but used by Sentry
    // Create a breadcrumb for user identification
    addBreadcrumb(`User identified: ${user.id}`, 'user', 'info', { user });
  },

  /**
   * Clear user information (e.g. on logout)
   */
  clearUser: () => {
    // This is a no-op in the logger itself, but used by Sentry
    addBreadcrumb('User logged out', 'user', 'info');
  },

  /**
   * Log silent refresh operations with distinctive styling
   */
  refresh: (message: string, data?: unknown) => {
    if (isDev) {
      if (data) {
        console.log(`🔄 %cRefresh%c ${message}`, colors.refresh, colors.default, data);
      } else {
        console.log(`🔄 %cRefresh%c ${message}`, colors.refresh, colors.default);
      }
    }

    // Add breadcrumb to Sentry
    addBreadcrumb(message, 'refresh', 'info', data as Record<string, unknown>);
  },

  /**
   * Log connectivity and online/offline status changes
   */
  connectivity: (message: string, data?: unknown) => {
    if (isDev) {
      if (data) {
        console.log(`🔌 %cConnectivity%c ${message}`, colors.connectivity, colors.default, data);
      } else {
        console.log(`🔌 %cConnectivity%c ${message}`, colors.connectivity, colors.default);
      }
    }
  },

  /**
   * Log data synchronization operations between online/offline
   */
  sync: (message: string, data?: unknown) => {
    if (isDev) {
      if (data) {
        console.log(`♻️ %cSync%c ${message}`, colors.sync, colors.default, data);
      } else {
        console.log(`♻️ %cSync%c ${message}`, colors.sync, colors.default);
      }
    }
  },
};
